import React, { useMemo } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useNavigate } from "react-router-dom";

import Navbar from "../../../globals/components/Navbar";

const cardSx = {
  borderRadius: 3,
  background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 50%, #f8f9fa 100%)",
  border: "1px solid rgba(37, 99, 235, 0.2)",
  transition: "all 0.3s ease-in-out",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  "&:hover": {
    boxShadow: "0 4px 16px rgba(37, 99, 235, 0.15)",
    background: "linear-gradient(135deg, #e9ecef 0%, #dee2e6 50%, #e9ecef 100%)",
    transform: "translateY(-2px)",
    borderColor: "rgba(37, 99, 235, 0.4)",
  },
};

function flattenLinks(links) {
  return Array.isArray(links[0]) ? links.flat() : links;
}

function ManagementLinkCard({ link, onNavigate }) {
  const isDisabled = Boolean(link.disabled);
  const Icon = link.icon;
  const cardInner = (
    <Card
      elevation={3}
      sx={{
        ...cardSx,
        opacity: isDisabled ? 0.55 : 1,
        "&:hover": isDisabled ? {} : cardSx["&:hover"],
      }}
    >
      <CardActionArea
        disabled={isDisabled}
        onClick={() => {
          if (!isDisabled) onNavigate(link.path);
        }}
      >
        <CardContent
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            padding: 3,
            color: "#333333",
          }}
        >
          {Icon ? <Icon fontSize="large" style={{ color: "#2563eb" }} /> : null}
          <Typography variant="h6" fontWeight={500} sx={{ color: "#333333" }}>
            {link.name}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );

  if (isDisabled && link.disabledHint) {
    return (
      <Tooltip title={link.disabledHint} placement="top" arrow followCursor>
        <span>{cardInner}</span>
      </Tooltip>
    );
  }

  return cardInner;
}

ManagementLinkCard.propTypes = {
  link: PropTypes.shape({
    name: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
    icon: PropTypes.elementType,
    disabled: PropTypes.bool,
    disabledHint: PropTypes.string,
    feature: PropTypes.string,
  }).isRequired,
  onNavigate: PropTypes.func.isRequired,
};

/**
 * Shared card-grid shell for Internal User and Admin User management pages.
 */
export default function IuManagementPageBase({
  title,
  links,
  filterByFeatures = false,
  userFeatures = [],
}) {
  const navigate = useNavigate();

  const visibleLinks = useMemo(() => {
    const flat = flattenLinks(links);
    if (!filterByFeatures) return flat;
    return flat.filter((link) => userFeatures.includes(link.feature));
  }, [links, filterByFeatures, userFeatures]);

  return (
    <>
      <Navbar />
      <Box sx={{ padding: 4 }}>
        <Typography variant="h4" fontWeight={800} mb={4} sx={{ color: "black" }}>
          {title}
        </Typography>
        <Grid container spacing={3}>
          {visibleLinks.map((link) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={link.path}>
              <ManagementLinkCard link={link} onNavigate={navigate} />
            </Grid>
          ))}
        </Grid>
      </Box>
    </>
  );
}

IuManagementPageBase.propTypes = {
  title: PropTypes.string.isRequired,
  links: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.object),
    PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.object)),
  ]).isRequired,
  filterByFeatures: PropTypes.bool,
  userFeatures: PropTypes.arrayOf(PropTypes.string),
};
